/*
# keyboard-gamepad

Maps keyboard and mouse input to a `VirtualGamepad`. Uses attack/decay smoothing
for responsive analog-feel from digital keys.

## Example

Turns WASD / arrows / mouse into a `VirtualGamepad` — the same spine the hardware pad and XR
controllers feed, so a keyboard "just works" anywhere a gamepad does:

```javascript
import { keyboardGamepad } from 'tosijs-3d'
// keyboardGamepad() → a source whose .poll() yields a VirtualGamepad from the keys below. Usually
// you don't touch it directly — <tosi-b3d-controller> wires it into the merged input for you.
```

## Default Key Map

| VirtualGamepad | Keys |
|----------------|------|
| leftStick Y | W (+) / S (-) |
| leftStick X | D (+) / A (-) |
| rightStick Y | ArrowUp (+) / ArrowDown (-) — LOOK, not zoom |
| rightStick X | ArrowRight (+) / ArrowLeft (-) |
| buttonA | Space |
| buttonB | F |
| buttonX | E |
| leftBumper | ShiftLeft |
| leftTrigger | Q |
| rightTrigger | R |
| buttonY | Y |
| rightBumper | ShiftRight |
| rightStickPress (R3) | V (GTA's camera key) |
| dpadUp/Down/Left/Right | I / M / J / K |

Mouse wheel adjusts the ZOOM axis (d-pad up/down), not the look stick.
*/

import { Component } from 'tosijs'
import type { VirtualGamepad, GamepadSource } from './virtual-gamepad.js'
import { emptyGamepad } from './virtual-gamepad.js'
import { isTextEntry } from './text-entry.js'
import { realNow } from './clock.js'

function keycode(evt: KeyboardEvent): string {
  return evt.code.replace(/Key|Digit/, '')
}

function clamp(min: number, x: number, max: number): number {
  return x < min ? min : x > max ? max : x
}

type AxisDef = {
  field: keyof VirtualGamepad
  positiveKeys: string[]
  negativeKeys: string[]
  attack: number
  decay: number
}

type ButtonDef = {
  field: keyof VirtualGamepad
  keys: string[]
  attack: number
  decay: number
  type?: 'toggle'
}

const DEFAULT_AXES: AxisDef[] = [
  {
    field: 'leftStickY',
    positiveKeys: ['W'],
    negativeKeys: ['S'],
    attack: 2,
    decay: 5,
  },
  {
    field: 'leftStickX',
    positiveKeys: ['D'],
    negativeKeys: ['A'],
    attack: 2,
    decay: 5,
  },
  {
    field: 'rightStickX',
    positiveKeys: ['ArrowRight'],
    negativeKeys: ['ArrowLeft'],
    attack: 2,
    decay: 5,
  },
  {
    field: 'rightStickY',
    positiveKeys: ['ArrowUp'],
    negativeKeys: ['ArrowDown'],
    attack: 2,
    decay: 5,
  },
]

const DEFAULT_BUTTONS: ButtonDef[] = [
  { field: 'buttonA', keys: ['Space'], attack: 5, decay: 10 },
  { field: 'buttonB', keys: ['F'], attack: 5, decay: 10 },
  { field: 'buttonX', keys: ['E'], attack: 5, decay: 10 },
  { field: 'leftBumper', keys: ['ShiftLeft'], attack: 5, decay: 10 },
  { field: 'leftTrigger', keys: ['Q'], attack: 5, decay: 10 },
  { field: 'rightTrigger', keys: ['R'], attack: 5, decay: 10 },
  { field: 'buttonY', keys: ['Y'], attack: 5, decay: 10 },
  { field: 'rightBumper', keys: ['ShiftRight'], attack: 5, decay: 10 },
  { field: 'rightStickPress', keys: ['V'], attack: 5, decay: 10 },
  { field: 'dpadUp', keys: ['I'], attack: 5, decay: 10 },
  { field: 'dpadDown', keys: ['M'], attack: 5, decay: 10 },
  { field: 'dpadLeft', keys: ['J'], attack: 5, decay: 10 },
  { field: 'dpadRight', keys: ['K'], attack: 5, decay: 10 },
]

export class KeyboardGamepadSource extends Component implements GamepadSource {
  static preferredTagName = 'tosi-keyboard-gamepad'

  readonly kind = 'keyboard'

  static initAttributes = {
    wheelSensitivity: 1,
    updateIntervalMs: 33,
  }

  private axes = DEFAULT_AXES
  private buttons = DEFAULT_BUTTONS
  private pressedKeys = new Set<string>()
  private axisState: Record<string, number> = {}
  private buttonState: Record<string, number> = {}
  private wheelAccum = 0
  private interval = 0
  private lastUpdate = 0

  poll(): VirtualGamepad {
    const pad = emptyGamepad()

    for (const axis of this.axes) {
      pad[axis.field] = this.axisState[axis.field] ?? 0
    }
    for (const btn of this.buttons) {
      ;(pad as any)[btn.field] = this.buttonState[btn.field] ?? 0
    }

    /*
    MOUSE WHEEL → THE ZOOM AXIS, WHICH IS THE D-PAD.

    It used to feed `rightStickY`, and that was right while the right stick WAS
    zoom. The stick became LOOK, and the wheel came along with it — so a scroll
    pitched the camera, and while swimming it tipped the swimmer, because the
    swim aim is the look pitch. Reported as swimming being "horribly broken",
    and the wheel is the last place anyone would look for it.

    Pointing the wheel at the zoom axis rather than at whatever the right stick
    currently means keeps "the wheel zooms" true through any future remapping.
    Merged with the keys via max-abs so both work.
    */
    const wheel = clamp(-1, this.wheelAccum, 1)
    if (wheel > Math.abs(pad.dpadUp)) pad.dpadUp = wheel
    else if (-wheel > Math.abs(pad.dpadDown)) pad.dpadDown = -wheel
    this.wheelAccum *= 0.8 // decay wheel toward 0

    return pad
  }

  private _isMappedKey(code: string): boolean {
    for (const axis of this.axes) {
      if (axis.positiveKeys.includes(code) || axis.negativeKeys.includes(code))
        return true
    }
    for (const btn of this.buttons) {
      if (btn.keys.includes(code)) return true
    }
    return false
  }

  private _handleKeyDown = (event: KeyboardEvent) => {
    /*
    NOT WHILE SOMEONE IS TYPING.

    This listens on `window` so you can steer without clicking the canvas first
    — right for a scene, and wrong for the rest of a page. `a` is a strafe key,
    so typing "lamp" into a search box lost the character to `preventDefault`
    AND strafed the demo while it was typed. Tonio, from an iPhone: "I was
    trying to search nav for lamp and typing didn't work. Are we somehow
    preventing regular keyboards from working?"

    Yes, we were. Asking the event where it came from is a property read and
    cannot go stale, which a "input is focused" flag would.
    */
    if (isTextEntry(event)) return
    const code = keycode(event)
    if (this._isMappedKey(code)) event.preventDefault()
    this.pressedKeys.add(code)

    // Handle toggles on press
    for (const btn of this.buttons) {
      if (btn.type !== 'toggle') continue
      if (btn.keys.some((k) => k === keycode(event))) {
        this.buttonState[btn.field] = 1 - (this.buttonState[btn.field] ?? 0)
      }
    }
  }

  private _handleKeyUp = (event: KeyboardEvent) => {
    // NOT guarded: a key pressed on the canvas and released after focus moved
    // into a field must still come up, or the entity strafes forever.
    this.pressedKeys.delete(keycode(event))
  }

  private _handleWheel = (event: WheelEvent) => {
    this.wheelAccum = clamp(
      -1,
      this.wheelAccum + event.deltaY * (this as any).wheelSensitivity * 0.01,
      1
    )
  }

  private _updateSmoothing = () => {
    const now = realNow()
    const dt = (now - this.lastUpdate) * 0.001
    this.lastUpdate = now

    // Axes: positive/negative keys produce -1..1
    for (const axis of this.axes) {
      const posPressed = axis.positiveKeys.some((k) => this.pressedKeys.has(k))
      const negPressed = axis.negativeKeys.some((k) => this.pressedKeys.has(k))

      let target = 0
      if (posPressed && !negPressed) target = 1
      else if (negPressed && !posPressed) target = -1

      const current = this.axisState[axis.field] ?? 0
      if (
        Math.abs(target) > Math.abs(current) ||
        Math.sign(target) !== Math.sign(current)
      ) {
        // Moving toward target: use attack rate
        const step = axis.attack * dt
        if (Math.abs(target - current) < step) {
          this.axisState[axis.field] = target
        } else {
          this.axisState[axis.field] =
            current + Math.sign(target - current) * step
        }
      } else {
        // Decaying toward target: use decay rate
        const step = axis.decay * dt
        if (Math.abs(target - current) < step) {
          this.axisState[axis.field] = target
        } else {
          this.axisState[axis.field] =
            current + Math.sign(target - current) * step
        }
      }
    }

    // Buttons (non-toggle): 0..1 with attack/decay
    for (const btn of this.buttons) {
      if (btn.type === 'toggle') continue
      const pressed = btn.keys.some((k) => this.pressedKeys.has(k))
      const current = this.buttonState[btn.field] ?? 0
      if (pressed) {
        this.buttonState[btn.field] = Math.min(1, current + btn.attack * dt)
      } else {
        this.buttonState[btn.field] = Math.max(0, current - btn.decay * dt)
      }
    }
  }

  connectedCallback() {
    super.connectedCallback()
    this.pressedKeys = new Set()
    this.axisState = {}
    this.buttonState = {}
    this.lastUpdate = realNow()
    this.interval = window.setInterval(
      this._updateSmoothing,
      (this as any).updateIntervalMs
    )
    window.addEventListener('keydown', this._handleKeyDown)
    window.addEventListener('keyup', this._handleKeyUp)
    window.addEventListener('wheel', this._handleWheel, {
      passive: false,
    })
  }

  disconnectedCallback() {
    clearInterval(this.interval)
    this.interval = 0
    window.removeEventListener('keydown', this._handleKeyDown)
    window.removeEventListener('keyup', this._handleKeyUp)
    window.removeEventListener('wheel', this._handleWheel)
    super.disconnectedCallback()
  }
}

export const keyboardGamepad = KeyboardGamepadSource.elementCreator()
